import { useEffect, useRef, useState } from 'react';
import { enviarCroqui, montarRelatorio, type CampanhaCompleta, type RelatorioCompleto } from '../../api/cptu';
import { numero, plural } from '../../formato';
import type { ControleDoMapa, Falha, Feicao } from '../../mapaGeotecnico';
import { aoMudarTemaEfetivo, temaEfetivo } from '../../tema';
import { Aviso, BarraDeAvanco, Dialogo, ErroDaApi } from '../../ui';
import { areaDaPrancha, descricaoDoGiro, giroNormal, giroQueDeitaOsPontos, PASSO_DO_GIRO } from './locacao';

/**
 * O croqui de locação capturado do mapa — onda 315, Tarefa 315.5 (spec 07 §7.4 e §13; R7).
 *
 * A janela da casa (`Dialogo`, larga) com o Mapa Geotécnico das verticais da campanha (o motor de sempre, sob demanda:
 * o MapLibre pesa e só esta janela o carrega aqui), no satélite na primeira vez (a escolha do fundo fica guardada no
 * aparelho, na chave do croqui). A pessoa enquadra; «Capturar» pede ao motor a imagem (`capturar`: o mapa vai ao norte
 * e ao plano, o quadro é lido, a vista volta) e mostra a prévia; «Usar este croqui» envia a imagem e a moldura
 * (`origem: 'mapa'`, o enquadramento, o zoom, os metros por pixel do canvas, a largura e a altura, o fundo e o crédito)
 * pelo `POST …/croqui`. O PDF desenha por cima o norte, a escala gráfica, a legenda e o crédito.
 *
 * Sem WebGL, ou com o satélite que recusa a leitura do canvas (a imagem de outra origem sem CORS), «Capturar» fica
 * bloqueado com o motivo, e o caminho é «Trocar por imagem» (na aba).
 *
 * Onda 338 (a locação pelo mapa, RN-267): a janela escolhe o FUNDO da prancha — «Branco» (o desenho vetorial: «Gravar»
 * grava só o fundo e o giro) ou «Satélite» (a captura, agora sem os símbolos do mapa: a prancha desenha por cima o
 * triângulo e o nome de cada vertical e a quadrícula) — e o GIRO: os botões ±15°, o ângulo digitado, «Ajustar aos
 * pontos» (a direção principal das verticais fica deitada na folha) e «Norte para cima». O mapa gira junto; no
 * satélite, o retângulo tracejado é a área que a prancha mostra (a captura sai norte para cima e a prancha a gira). O
 * fundo e o giro vão às páginas do relatório (`pranchaFundo`, `pranchaGiro`).
 */

/** O fundo do croqui fica guardado no aparelho, nesta chave (à parte da do Mapa Geotécnico). */
const CHAVE_DO_FUNDO = 'labmanager.cptu.croqui.fundo';
const CHAVE_DAS_CAMADAS = 'labmanager.cptu.croqui.camadas';
/** O lado do ícone, como no mapa do Hub. */
const TAMANHO_DO_ICONE = 30;
/** Blumenau, se nenhuma vertical tivesse coordenada (a aba não abre a janela nesse caso). */
const CASA: [number, number] = [-26.9194, -49.0661];

/** O texto que vai a uma dica HTML do mapa, escapado. */
const escapar = (s: string) => s.replace(/[&<>"']/g, (x) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[x]!);

/** O croqui do satélite, na primeira vez: a casa decide o fundo pelo que o relatório pede (spec 07 §7.4). */
function satelitePrimeiro() {
  try {
    if (!localStorage.getItem(CHAVE_DO_FUNDO)) localStorage.setItem(CHAVE_DO_FUNDO, 'satelite');
  } catch { /* sem memória no aparelho: o mapa abre no fundo Mapa, e a pessoa troca */ }
}

/** As verticais ativas com coordenada — os pontos do croqui. */
export function verticaisComCoordenada(c: CampanhaCompleta) {
  return c.verticais.filter((v) => v.situacao === 'ativa' && v.latitude !== null && v.longitude !== null);
}

/** O navegador desenha o mapa? (o MapLibre 6 só desenha em WebGL 2) */
export function temWebgl(): boolean {
  try {
    const gl = document.createElement('canvas').getContext('webgl2');
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
    return !!gl;
  } catch { return false; }
}

type Captura = NonNullable<Awaited<ReturnType<ControleDoMapa['capturar']>>>;

const MOTIVO_SEM_WEBGL = 'Este navegador não desenha o mapa (sem WebGL): envie uma imagem por «Trocar por imagem».';
const MOTIVO_RECUSADA = 'O fundo de satélite não deixou ler a imagem (o servidor das imagens recusou a captura): '
  + 'troque o fundo para «Mapa» no canto do mapa e capture de novo, ou envie uma imagem.';

export function CapturaDoCroqui({ c, r, aoFechar, aoPronto }: {
  c: CampanhaCompleta; r: RelatorioCompleto; aoFechar(): void; aoPronto(): Promise<void>;
}) {
  const [fundo, setFundo] = useState<'branco' | 'satelite'>(r.paginas.pranchaFundo === 'branco' ? 'branco' : 'satelite');
  const [giro, setGiro] = useState(() => giroNormal(r.paginas.pranchaGiro ?? 0));
  const [giroDigitado, setGiroDigitado] = useState<string | null>(null);
  const [tamanho, setTamanho] = useState<{ largura: number; altura: number } | null>(null);
  const [gravando, setGravando] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const [controle, setControle] = useState<ControleDoMapa | null>(null);
  const [falhas, setFalhas] = useState<Set<Falha>>(new Set());
  const [semMotor, setSemMotor] = useState(false);
  const [captura, setCaptura] = useState<Captura | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);
  const [recusada, setRecusada] = useState(false);
  const [capturando, setCapturando] = useState(false);
  const [progresso, setProgresso] = useState<number | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const pontos = verticaisComCoordenada(c);

  useEffect(() => {
    let vivo = true;
    let criado: ControleDoMapa | null = null;
    let desligarTema: (() => void) | null = null;
    satelitePrimeiro();
    void (async () => {
      const { criarMapa } = await import('../../mapaGeotecnico');
      if (!vivo || !caixa.current) return;
      const m = await criarMapa(caixa.current, {
        casa: CASA,
        chaveDoFundo: CHAVE_DO_FUNDO,
        chaveDasCamadas: CHAVE_DAS_CAMADAS,
        tema: temaEfetivo(),
        tamanhoDoIcone: TAMANHO_DO_ICONE,
        dica: (f) => `<strong>${escapar(f.nome)}</strong>`,
        textoDaLista: (f) => `Vertical ${f.nome}`,
      });
      if (!vivo) { m.destruir(); return; }
      criado = m;
      m.definirRegistros(pontos.map((v): Feicao => ({
        chave: v.id, lugar: v.id, tipo: 'cptu', estado: 'exec', fonte: 'ponto', estimada: false,
        nome: v.codigo, cliente: c.os.cliente ?? '', latitude: v.latitude as number, longitude: v.longitude as number,
      })));
      m.enquadrar();
      // O giro gravado no relatório; o mapa avisa cada giro (botões, bússola, gestos do 3D).
      if (giro) m.girar(giro);
      m.aoGirar((g) => setGiro(giroNormal(g)));
      m.aoFalhar((f, falhou) => setFalhas((antes) => {
        if (antes.has(f) === falhou) return antes;
        const novo = new Set(antes);
        if (falhou) novo.add(f); else novo.delete(f);
        return novo;
      }));
      desligarTema = aoMudarTemaEfetivo((t) => m.definirTema(t));
      setControle(m);
    })().catch((e) => {
      console.error('croqui: o motor do mapa não carregou', e);
      if (vivo) setSemMotor(true);
    });
    return () => {
      vivo = false;
      desligarTema?.();
      criado?.destruir();
    };
    // O mapa nasce uma vez por janela: as verticais são as da campanha aberta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* O tamanho da caixa do mapa, para a área da prancha. */
  useEffect(() => {
    const el = caixa.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ler = () => setTamanho({ largura: el.clientWidth, altura: el.clientHeight });
    ler();
    const o = new ResizeObserver(ler);
    o.observe(el);
    return () => o.disconnect();
  }, []);

  /** Gira o mapa (e a prancha) para o ângulo dado. */
  const girarPara = (g: number) => {
    const novo = giroNormal(g);
    setGiro(novo);
    setGiroDigitado(null);
    controle?.girar(novo);
  };

  /* A prévia é um endereço local: solto quando muda ou quando a janela fecha. */
  useEffect(() => () => { if (previa) URL.revokeObjectURL(previa); }, [previa]);

  const semWebgl = falhas.has('webgl') || falhas.has('motor') || semMotor;
  const motivo = semWebgl ? MOTIVO_SEM_WEBGL : recusada ? MOTIVO_RECUSADA : null;

  const capturar = async () => {
    if (!controle || capturando || motivo) return;
    setCapturando(true); setErro(null);
    try {
      // Uma segunda tentativa antes de dizer que a imagem foi recusada (onda 338: o quadro que não chegou a tempo).
      const x = await controle.capturar({ tipo: 'image/png', semRegistros: true })
        ?? await controle.capturar({ tipo: 'image/png', semRegistros: true });
      if (!x) { setRecusada(true); return; }
      setCaptura(x);
      setPrevia(URL.createObjectURL(x.blob));
    } finally { setCapturando(false); }
  };

  const usar = async () => {
    if (!captura || progresso !== null) return;
    setErro(null); setProgresso(0);
    try {
      await montarRelatorio(c.campanha.id, { paginas: { ...r.paginas, pranchaFundo: 'satelite', pranchaGiro: giro } });
    } catch (e) { setErro(e); setProgresso(null); return; }
    const fd = new FormData();
    fd.append('imagem', captura.blob, 'croqui.png');
    fd.append('meta', JSON.stringify({
      origem: 'mapa', metrosPorPixel: captura.metrosPorPixel, largura: captura.largura, altura: captura.altura,
      enquadramento: captura.enquadramento, zoom: captura.zoom, fundo: captura.fundo, credito: captura.credito,
    }));
    try {
      await enviarCroqui(c.campanha.id, fd, (f) => setProgresso(f));
      await aoPronto();
    } catch (e) { setErro(e); } finally { setProgresso(null); }
  };

  /** O fundo branco: grava o fundo e o giro nas páginas do relatório (nada a capturar). */
  const gravarBranco = async () => {
    if (gravando) return;
    setGravando(true); setErro(null);
    try {
      await montarRelatorio(c.campanha.id, { paginas: { ...r.paginas, pranchaFundo: 'branco', pranchaGiro: giro } });
      await aoPronto();
    } catch (e) { setErro(e); } finally { setGravando(false); }
  };

  const area = fundo === 'satelite' && tamanho && tamanho.largura > 0 && tamanho.altura > 0
    ? areaDaPrancha(tamanho.largura, tamanho.altura, giro) : null;
  const escala = captura ? captura.metrosPorPixel * captura.largura : null;
  return (
    <Dialogo aberto larga titulo="Capturar o croqui do mapa" rotulo="Croqui de locação" aoFechar={aoFechar}>
      <>
        <p className="sub">
          {fundo === 'satelite'
            ? 'Enquadre as verticais dentro do retângulo tracejado: é a área da prancha. A prancha desenha por cima o nome de cada vertical, a quadrícula, o norte, a escala gráfica e o crédito do fundo.'
            : 'No fundo branco, a prancha desenha as verticais com o nome de cada uma, a quadrícula, o norte e a escala gráfica, enquadradas pelos pontos; aqui se escolhe o giro.'}
          {' '}{plural(pontos.length, 'vertical com coordenada', 'verticais com coordenada')}.
        </p>
        {!captura && (
          <div className="cptu-croqui-controles">
            <div className="campo">
              <span className="rotulo" id="cptu-croqui-fundo">Fundo da prancha</span>
              <div className="seg" role="group" aria-labelledby="cptu-croqui-fundo">
                <button type="button" aria-pressed={fundo === 'branco'} onClick={() => setFundo('branco')}>Branco</button>
                <button type="button" aria-pressed={fundo === 'satelite'} onClick={() => setFundo('satelite')}>Satélite</button>
              </div>
            </div>
            <div className="campo cptu-croqui-giro" data-giro={giro}>
              <span className="rotulo" id="cptu-croqui-giro">Giro da prancha</span>
              <div className="cptu-croqui-giro-linha" role="group" aria-labelledby="cptu-croqui-giro">
                <button type="button" className="botao secundario" aria-label={`Girar ${PASSO_DO_GIRO}° para a esquerda`}
                  onClick={() => girarPara(giro - PASSO_DO_GIRO)}>−{PASSO_DO_GIRO}°</button>
                <label className="cptu-croqui-angulo">
                  <span className="so-leitor">Ângulo do giro, em graus no sentido horário</span>
                  <input type="number" inputMode="decimal" min={-180} max={180} step={1}
                    value={giroDigitado ?? String(giro)}
                    onChange={(e) => setGiroDigitado(e.target.value)}
                    onBlur={() => { if (giroDigitado !== null) girarPara(Number(giroDigitado.replace(',', '.')) || 0); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && giroDigitado !== null) { e.preventDefault(); girarPara(Number(giroDigitado.replace(',', '.')) || 0); } }} />
                  <span aria-hidden="true">°</span>
                </label>
                <button type="button" className="botao secundario" aria-label={`Girar ${PASSO_DO_GIRO}° para a direita`}
                  onClick={() => girarPara(giro + PASSO_DO_GIRO)}>+{PASSO_DO_GIRO}°</button>
                <button type="button" className="botao secundario"
                  onClick={() => girarPara(giroQueDeitaOsPontos(pontos.map((v) => ({ latitude: v.latitude as number, longitude: v.longitude as number }))))}>
                  Ajustar aos pontos
                </button>
                <button type="button" className="botao secundario" aria-disabled={giro === 0} onClick={() => girarPara(0)}>Norte para cima</button>
              </div>
              <small className="det" data-giro-texto>{giro === 0 ? 'Norte para cima.' : `Prancha ${descricaoDoGiro(giro)}: o norte segue a seta, e os nomes ficam de pé.`}</small>
            </div>
          </div>
        )}
        <ErroDaApi erro={erro} />
        {falhas.has('satelite') && !semWebgl && (
          <Aviso tipo="info">O satélite não carregou agora: a captura sai do que está desenhado. Troque o fundo no canto do mapa, se quiser.</Aviso>
        )}
        <div className="cptu-croqui-moldura" hidden={!!captura}>
          <div ref={caixa} className="mapa-caixa cptu-croqui-mapa" />
          {area && (
            <div className="cptu-croqui-area" aria-hidden="true" data-largura={Math.round(area.largura)} data-altura={Math.round(area.altura)}
              style={{ width: `${area.largura}px`, height: `${area.altura}px` }} />
          )}
        </div>
        {captura && previa && (
          <figure className="cptu-croqui-previa">
            <img src={previa} alt="A prévia do croqui capturado" />
            <figcaption>
              {numero(captura.largura, 0)} × {numero(captura.altura, 0)} pixels
              {escala !== null && <> · {numero(escala, 0)} m de leste a oeste</>}
              {captura.credito && <> · {captura.credito}</>}
            </figcaption>
          </figure>
        )}
        {progresso !== null && <BarraDeAvanco pct={progresso * 100} rotulo="Enviando o croqui" />}
        {motivo && !captura && <p className="cptu-motivo-bloqueio" id="cptu-croqui-motivo">{motivo}</p>}
        <div className="rodape-dialogo">
          {captura ? (
            <>
              <button type="button" className="botao secundario" onClick={() => { setCaptura(null); setPrevia(null); window.setTimeout(() => controle?.redimensionar(), 0); }}>
                Capturar de novo
              </button>
              <button type="button" className="botao" aria-disabled={progresso !== null} onClick={() => { void usar(); }}>
                {progresso !== null ? 'Enviando…' : 'Usar este croqui'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
              {fundo === 'branco' ? (
                <button type="button" className="botao" aria-disabled={gravando} onClick={() => { void gravarBranco(); }}>
                  {gravando ? 'Gravando…' : 'Gravar'}
                </button>
              ) : motivo ? (
                <button type="button" className="botao" aria-disabled="true" aria-describedby="cptu-croqui-motivo">
                  Capturar
                </button>
              ) : (
                <button type="button" className="botao" aria-disabled={!controle || capturando}
                  onClick={() => { void capturar(); }}>
                  {capturando ? 'Capturando…' : 'Capturar'}
                </button>
              )}
            </>
          )}
        </div>
      </>
    </Dialogo>
  );
}
